import { TIconName } from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import { DistributiveOmit } from '@payaca/utilities/types';
import React, {
  FC,
  forwardRef,
  FunctionComponent,
  PropsWithChildren,
  useContext,
  useMemo,
} from 'react';
import PlAnchorButton, { AnchorButtonProps } from '../Button/AnchorButton';
import PlButton, { ButtonProps } from '../Button/Button';
import {
  default as BasicInput,
  InputIcon,
  type InputProps,
} from '../Input/Input';
import {
  default as RawInputBase,
  type InputSizeVariant,
  type RawInputProps as TRawInputProps,
} from '../Input/RawInput';
import BasicSelect, {
  SelectProps,
  SelectSizeVariant,
  ValueType,
} from '../Select/Select';

export type InputGroupProps = {
  sizeVariant?: InputSizeVariant;
  className?: string;
};

export const InputGroupContext = React.createContext<{
  sizeVariant: InputSizeVariant;
}>({
  sizeVariant: 'md',
});

const sharedClasses =
  'rounded-l-none rounded-r-none first:rounded-l-md last:rounded-r-md -ml-px first:ml-0';

const InputGroup: FunctionComponent<PropsWithChildren<InputGroupProps>> = ({
  sizeVariant = 'md',
  children,
  className,
}) => {
  return (
    <InputGroupContext.Provider value={{ sizeVariant }}>
      <div
        className={clstx(
          `relative w-full rounded-lg shadow-sm flex`,
          className
        )}
      >
        {children}
      </div>
    </InputGroupContext.Provider>
  );
};

const Text: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const { sizeVariant } = useContext(InputGroupContext);
  const sizeClasses = useMemo(() => {
    switch (sizeVariant) {
      case 'sm':
        return 'py-2';
      case 'md':
        return 'py-3';
      case 'lg':
        return 'py-3 sm:py-5';
    }
  }, [sizeVariant]);

  return (
    <span
      className={`${sizeClasses} inline-flex min-w-fit items-center px-4 ${sharedClasses} border border-gray-200 bg-gray-50 text-base`}
    >
      {children}
    </span>
  );
};

const Icon: FunctionComponent<{ name: TIconName }> = ({ name }) => {
  return (
    <Text>
      <InputIcon name={name} />
    </Text>
  );
};

export type InputGroupInput = Omit<
  InputProps,
  'styleVariant' | 'shapeVariant' | 'sizeVariant'
>;

const Input: FunctionComponent<InputGroupInput> = ({ className, ...props }) => {
  const { sizeVariant } = useContext(InputGroupContext);

  return (
    <BasicInput
      sizeVariant={sizeVariant}
      className={`${sharedClasses} w-full grow focus-within:z-10 ${className}`}
      {...props}
    />
  );
};

export type InputGroupRawInput = Omit<
  TRawInputProps,
  'styleVariant' | 'shapeVariant' | 'sizeVariant'
>;

const RawInput = React.forwardRef<HTMLInputElement, InputGroupRawInput>(
  (props, ref) => {
    const { className, ...rest } = props;
    const { sizeVariant } = useContext(InputGroupContext);

    return (
      <RawInputBase
        ref={ref}
        sizeVariant={sizeVariant}
        className={clstx(
          sharedClasses,
          'w-full grow focus-within:z-10',
          className
        )}
        {...rest}
      />
    );
  }
);

const Select: FunctionComponent<
  Omit<SelectProps<ValueType, boolean>, 'sizeVariant'>
> = ({ className, ...props }) => {
  const { sizeVariant } = useContext(InputGroupContext);

  const selectSizeVariant = useMemo(() => {
    switch (sizeVariant) {
      case 'sm':
        return SelectSizeVariant.SM;
      case 'md':
        return SelectSizeVariant.MD;
      case 'lg':
        return SelectSizeVariant.LG;
    }
  }, [sizeVariant]);

  return (
    <BasicSelect
      {...props}
      sizeVariant={selectSizeVariant}
      className={`${sharedClasses} grow shadow-none focus-within:z-10 ${className}`}
    />
  );
};

const Button = forwardRef<
  HTMLButtonElement,
  DistributiveOmit<ButtonProps, 'size'>
>(({ children, className, ...rest }, ref) => {
  const { sizeVariant } = useContext(InputGroupContext);

  return (
    <PlButton
      ref={ref}
      size={sizeVariant}
      className={
        sharedClasses +
        ' focus-within:z-10' +
        (className ? ' ' + className : '')
      }
      {...rest}
    >
      {children}
    </PlButton>
  );
});

const AnchorButton: FC<
  DistributiveOmit<AnchorButtonProps, 'size'> & { className?: string }
> = (props) => {
  const { className, ...rest } = props;

  const { sizeVariant } = useContext(InputGroupContext);

  return (
    <PlAnchorButton
      size={sizeVariant}
      className={
        sharedClasses +
        ' focus-within:z-10' +
        (className ? ' ' + className : '')
      }
      {...rest}
    />
  );
};

export default Object.assign(InputGroup, {
  Input,
  RawInput,
  Text,
  Icon,
  Select,
  Button,
  AnchorButton,
});
